import axios from 'axios';

/**
 * DentalAIService - Service for integrating with dental AI analysis API
 * 
 * API Documentation:
 * - Endpoint: http://203.157.65.56:7744/detect_all
 * - Method: POST
 * - Authentication: X-API-KEY header
 * - Input: form-data with image files
 * - Output: JSON with detected dental problems
 */
class DentalAIService {
  constructor() {
    // Use proxy endpoint instead of direct external API call
    this.apiUrl = '/api/dental-ai/analyze';
    this.defaultThreshold = 0.5;

    // Using centralized axios configuration (no custom instance needed)
    // Timeout, auth, base URL, and error handling are handled centrally
  }

  /**
   * Analyze dental photos using AI API
   * @param {Object} params - Analysis parameters
   * @param {string} params.upperPhoto - Base64 data URL or HTTPS URL (S3) of upper occlusal photo
   * @param {string} params.lowerPhoto - Base64 data URL or HTTPS URL (S3) of lower occlusal photo
   * @param {number} params.threshold - Confidence threshold (0-1)
   * @param {string} params.patientId - Patient ID for tracking
   * @returns {Promise<Object>} Analysis results
   */
  async analyzePhotos({ upperPhoto, lowerPhoto, threshold = this.defaultThreshold, patientId }) {
    try {
      // Analyze both photos with individual error handling
      let upperResults, lowerResults;

      try {
        [upperResults, lowerResults] = await Promise.all([
          this.analyzeSinglePhoto(upperPhoto, threshold, 'upper'),
          this.analyzeSinglePhoto(lowerPhoto, threshold, 'lower')
        ]);
      } catch (error) {
        // If Promise.all fails, try individual processing
        try {
          upperResults = await this.analyzeSinglePhoto(upperPhoto, threshold, 'upper');
        } catch (upperError) {
          console.error('Upper photo processing failed:', upperError);
          upperResults = {
            photoType: 'upper',
            success: false,
            error: { code: 'UPPER_PHOTO_ERROR', message: 'Upper photo analysis failed' },
            data: { detections: {} }
          };
        }

        try {
          lowerResults = await this.analyzeSinglePhoto(lowerPhoto, threshold, 'lower');
        } catch (lowerError) {
          console.error('Lower photo processing failed:', lowerError);
          lowerResults = {
            photoType: 'lower',
            success: false,
            error: { code: 'LOWER_PHOTO_ERROR', message: 'Lower photo analysis failed' },
            data: { detections: {} }
          };
        }
      }

      // Combine and process results
      const combinedResults = this.combineAnalysisResults(upperResults, lowerResults);
      
      return {
        success: true,
        analysisId: `ai_${patientId}_${Date.now()}`,
        timestamp: new Date().toISOString(),
        patientId,
        results: combinedResults,
        summary: this.generateAnalysisSummary(combinedResults)
      };
      
    } catch (error) {
      console.error('AI analysis failed:', error);
      
      return {
        success: false,
        error: this.handleApiError(error),
        fallbackData: this.generateFallbackAnalysis()
      };
    }
  }

  /**
   * Analyze a single photo using the AI API
   * @param {string} photoData - Base64 data URL or HTTPS URL (e.g., S3 URL)
   * @param {number} threshold - Confidence threshold
   * @param {string} photoType - 'upper' or 'lower'
   * @returns {Promise<Object>} API response data
   */
  async analyzeSinglePhoto(photoData, threshold, photoType) {
    // Validate photo data
    if (!photoData) {
      return {
        photoType,
        success: false,
        error: { code: 'NO_PHOTO_DATA', message: `${photoType} photo data is missing` },
        data: { detections: {} }
      };
    }

    // Determine if photoData is a URL or base64, then convert to blob
    let blob;
    try {
      // Check if it's a URL (starts with http:// or https://)
      const isUrl = photoData.startsWith('http://') || photoData.startsWith('https://');

      if (isUrl) {
        console.log(`${photoType} photo is a URL, downloading...`);
        blob = await this.urlToBlob(photoData);
      } else if (photoData.startsWith('data:')) {
        console.log(`${photoType} photo is base64, converting...`);
        blob = this.base64ToBlob(photoData);
      } else {
        throw new Error('Invalid photo format. Must be either a URL (http/https) or base64 data URL');
      }
    } catch (blobError) {
      console.error(`${photoType} photo conversion failed:`, blobError);
      return {
        photoType,
        success: false,
        error: {
          code: 'PHOTO_CONVERSION_ERROR',
          message: `Failed to process ${photoType} photo: ${blobError.message}`
        },
        data: { detections: {} }
      };
    }
    
    // Create form data
    const formData = new FormData();
    formData.append('image', blob, `${photoType}_occlusal.jpg`);
    formData.append('threshold', threshold.toString());
    
    try {
      const response = await axios.post(this.apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return {
        photoType,
        success: true,
        data: response.data
      };

    } catch (error) {
      console.error(`Failed to analyze ${photoType} photo:`, error);

      return {
        photoType,
        success: false,
        error: this.handleApiError(error),
        data: { detections: {} }
      };
    }
  }

  /**
   * Combine analysis results from both photos
   * @param {Object} upperResults - Upper photo analysis results
   * @param {Object} lowerResults - Lower photo analysis results
   * @returns {Object} Combined analysis results
   */
  combineAnalysisResults(upperResults, lowerResults) {
    const combined = {
      upper: upperResults,
      lower: lowerResults,
      detections: {
        gum: [],
        carries: [],
        calculus: [],
        tongue: [],
        worn_teeth: [],
        fluorosis: []
      },
      totalDetections: 0
    };

    // Combine detections from both photos
    [upperResults, lowerResults].forEach(result => {
      if (result.success && result.data && result.data.detections) {
        Object.keys(result.data.detections).forEach(category => {
          if (result.data.detections[category] && Array.isArray(result.data.detections[category])) {
            // Add photo type to each detection for tracking
            const detectionsWithPhotoType = result.data.detections[category].map((detection, index) => ({
              ...detection,
              photoType: result.photoType,
              id: `${result.photoType}_${category}_${index}`
            }));
            
            // Initialize category if it doesn't exist
            if (!combined.detections[category]) {
              combined.detections[category] = [];
            }
            
            combined.detections[category].push(...detectionsWithPhotoType);
          }
        });
      }
    });

    // Calculate total detections
    combined.totalDetections = Object.values(combined.detections)
      .reduce((total, category) => total + category.length, 0);

    return combined;
  }

  /**
   * Generate analysis summary
   * @param {Object} results - Combined analysis results
   * @returns {Object} Analysis summary
   */
  generateAnalysisSummary(results) {
    const summary = {
      totalProblems: results.totalDetections,
      categoryCount: {},
      highConfidenceCount: 0,
      overallOralHealth: 'good'
    };

    // Count detections by category
    Object.keys(results.detections).forEach(category => {
      const count = results.detections[category].length;
      summary.categoryCount[category] = count;
      
      // Count high confidence detections
      summary.highConfidenceCount += results.detections[category]
        .filter(detection => detection.confidence > 0.7).length;
    });

    // Determine overall oral health
    if (summary.totalProblems === 0) {
      summary.overallOralHealth = 'excellent';
    } else if (summary.totalProblems <= 2) {
      summary.overallOralHealth = 'good';
    } else if (summary.totalProblems <= 5) {
      summary.overallOralHealth = 'fair';
    } else {
      summary.overallOralHealth = 'poor';
    }

    return summary;
  }

  /**
   * Generate fallback analysis when API fails
   * @returns {Object} Fallback analysis data
   */
  generateFallbackAnalysis() {
    return {
      detections: {
        gum: [],
        carries: [],
        calculus: [],
        tongue: [],
        worn_teeth: [],
        fluorosis: []
      },
      totalDetections: 0,
      message: 'Unable to complete AI analysis. Please proceed with manual examination.'
    };
  }

  /**
   * Convert base64 data URL to blob
   * @param {string} dataUrl - Base64 data URL
   * @returns {Blob} Blob object
   */
  base64ToBlob(dataUrl) {
    if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
      throw new Error('Invalid base64 data URL format. Expected format: data:image/...;base64,...');
    }

    try {
      const [header, base64Data] = dataUrl.split(',');
      if (!header || !base64Data) {
        throw new Error('Invalid data URL structure');
      }

      const mimeMatch = header.match(/:(.*?);/);
      if (!mimeMatch) {
        throw new Error('Could not extract MIME type');
      }

      const mime = mimeMatch[1];
      const bstr = atob(base64Data);
      const u8arr = new Uint8Array(bstr.length);

      for (let i = 0; i < bstr.length; i++) {
        u8arr[i] = bstr.charCodeAt(i);
      }

      return new Blob([u8arr], { type: mime });
    } catch (error) {
      throw new Error(`Base64 conversion failed: ${error.message}`);
    }
  }

  /**
   * Download image from URL and convert to blob
   * @param {string} imageUrl - HTTPS URL of the image (e.g., S3 URL)
   * @returns {Promise<Blob>} Blob object
   */
  async urlToBlob(imageUrl) {
    if (!imageUrl || typeof imageUrl !== 'string') {
      throw new Error('Invalid URL');
    }

    if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
      throw new Error('URL must start with http:// or https://');
    }

    try {
      console.log(`Downloading image from URL: ${imageUrl.substring(0, 100)}...`);

      const response = await axios.get(imageUrl, {
        responseType: 'blob',
        timeout: 30000 // 30 seconds timeout for image download
      });

      if (!response.data) {
        throw new Error('No image data received from URL');
      }

      console.log(`Image downloaded successfully. Size: ${response.data.size} bytes, Type: ${response.data.type}`);

      return response.data;
    } catch (error) {
      console.error('Failed to download image from URL:', error);
      throw new Error(`Failed to download image: ${error.message}`);
    }
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @param {Error} error - API error
   * @returns {Object} Error details
   */
  handleApiError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return {
            code: 'BAD_REQUEST',
            message: 'รูปภาพไม่ถูกต้องหรือไม่สามารถประมวลผลได้',
            details: data.error || 'Invalid image file'
          };
        case 401:
        case 403:
          return {
            code: 'UNAUTHORIZED',
            message: 'ไม่สามารถเข้าถึงระบบ AI ได้ กรุณาติดต่อผู้ดูแลระบบ',
            details: data.error || 'API key invalid'
          };
        case 500:
          return {
            code: 'SERVER_ERROR',
            message: 'ระบบ AI ขัดข้อง กรุณาลองใหม่อีกครั้ง',
            details: data.error || 'Internal server error'
          };
        default:
          return {
            code: 'API_ERROR',
            message: 'เกิดข้อผิดพลาดในการวิเคราะห์',
            details: data.error || error.message
          };
      }
    } else if (error.request) {
      return {
        code: 'NETWORK_ERROR',
        message: 'ไม่สามารถเชื่อมต่อกับระบบ AI ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
        details: 'Network timeout or connection failed'
      };
    } else {
      return {
        code: 'UNKNOWN_ERROR',
        message: 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
        details: error.message
      };
    }
  }

  /**
   * Validate photo data before sending to API
   * @param {string} photoData - Base64 data URL
   * @returns {Object} Validation result
   */
  validatePhotoData(photoData) {
    if (!photoData) {
      return { isValid: false, message: 'ไม่พบข้อมูลรูปภาพ' };
    }

    if (!photoData.startsWith('data:image/')) {
      return { isValid: false, message: 'รูปภาพไม่ถูกต้อง' };
    }

    // Check file size (approximate)
    const base64Length = photoData.split(',')[1].length;
    const fileSizeBytes = (base64Length * 3) / 4;
    const fileSizeMB = fileSizeBytes / (1024 * 1024);

    if (fileSizeMB > 10) {
      return { isValid: false, message: 'รูปภาพมีขนาดใหญ่เกินไป (สูงสุด 10MB)' };
    }

    return { isValid: true };
  }

  /**
   * Get supported problem categories with Thai descriptions
   * @returns {Object} Problem categories
   */
  getProblemCategories() {
    return {
      gum: {
        code: 'gum',
        nameTh: 'เหงือก',
        nameEn: 'Gum',
        conditions: ['เหงือกอักเสบ']
      },
      carries: {
        code: 'carries',
        nameTh: 'ฟันผุ',
        nameEn: 'Caries',
        conditions: ['ฟันผุ', 'อุดฟัน']
      },
      calculus: {
        code: 'calculus',
        nameTh: 'คราบหินปูน',
        nameEn: 'Calculus',
        conditions: ['คราบหินปูน']
      },
      tongue: {
        code: 'tongue',
        nameTh: 'ลิ้น',
        nameEn: 'Tongue',
        conditions: ['ลิ้นแผนที่', 'ลิ้นเลี่ยน', 'ลิ้นแตกร่อง']
      },
      worn_teeth: {
        code: 'worn_teeth',
        nameTh: 'ฟันสึก',
        nameEn: 'Worn Teeth',
        conditions: ['คอฟันสึก', 'ฟันสึก']
      },
      fluorosis: {
        code: 'fluorosis',
        nameTh: 'ฟันตกกระ',
        nameEn: 'Fluorosis',
        conditions: ['ฟันตกกระ']
      }
    };
  }
}

// Export singleton instance
export default new DentalAIService();